/**
 * useADLForm Hook
 * CCH Care — Phase 3
 *
 * Manages form state for a single ADL section.
 * Handles dirty tracking, auto-save debounce, and save status.
 * Used once per section — each section mounts its own instance.
 *
 * Dirty-state guard on initialData re-sync (2026-06-18):
 *   The re-sync effect below re-seeded formData (and cleared isDirty)
 *   whenever the serialized initialData changed — with NO check for
 *   unsaved local edits. Any background refresh that changed the parent's
 *   section data while a CNA was mid-edit therefore silently overwrote
 *   their in-progress input with the server's older copy. Two triggers in
 *   practice: the 60s ADL auto-refresh (useADLRecord) and a mid-edit
 *   liveShift/shift re-pull (ADLFlowSheetPage fetchRecord) — the latter
 *   fires during the grace-period late-doc flow when the shift rolls over.
 *   Reproduced as Hygiene chips wiped by the 60s poll.
 *
 *   Fix: the re-sync now skips adopting incoming initialData while the
 *   form has unsaved edits (isDirty), preserving the user's input. A clean
 *   form still re-seeds, so it stays fresh with server updates between
 *   saves. isDirty is read through isDirtyRef (NOT the effect's dep array)
 *   on purpose: if it were a dependency, the dirty→clean transition right
 *   after an autosave would re-run this effect against a stale initialData
 *   captured by a poll mid-edit, and re-seed the just-saved work away.
 *   Reading via ref means the effect only fires on a genuine initialData
 *   change, by which point the server reflects the save. This is the
 *   source-level fix for the section-form data-loss class and covers both
 *   refresh triggers. Affected sections are the ones built on this hook
 *   (Hygiene, Safety Checks, Bowel, Urination); the plain useState-based
 *   forms (Repositioning, Fluid Intake, Skin Checks) seed once and were
 *   never exposed.
 *
 * Failed-save retry-storm guard (2026-06-24):
 *   The autosave effect re-ran on every saveStatus change. On a save that
 *   the server rejects (e.g. urination POST with output_type missing, a
 *   400 "output_type is required"), save() set saveStatus 'saving'→'error'
 *   but left isDirty true. The 'saving'→'error' transition re-ran the
 *   autosave effect, which rescheduled the same invalid payload 1500ms
 *   later — an unbounded retry loop hammering the endpoint (and the audit
 *   log) until the tab closed. Observed live as a wall of 400s on
 *   ?action=urination after browser autofill dropped an email into the
 *   notes field and flipped the section dirty with no output_type set.
 *
 *   Fix (two layers):
 *   1. The autosave effect now also early-returns on saveStatus === 'error'.
 *      A rejected save no longer reschedules itself. The form stays dirty
 *      and the error surfaces via saveError/hasError; the NEXT user edit
 *      (setField/setFields resets saveStatus to 'idle') re-arms autosave.
 *      So edits are still retried — but only when the user changes
 *      something, never as a silent infinite loop against an unchanged
 *      payload the server already refused.
 *   2. Optional `validate(formData)` predicate (see options). When it
 *      returns false, save() is a no-op: nothing is sent. This stops a
 *      contract-invalid payload from ever reaching the server in the first
 *      place. Urination passes `(d) => !!d.output_type` since the backend
 *      requires output_type unconditionally. Default null = always valid
 *      (no behavior change for other sections).
 *
 * ESLint fixes:
 * - initialData sync uses lastSerializedRef to avoid JSON.stringify
 *   as a dependency expression (react-hooks/exhaustive-deps)
 * - Autosave effect calls save via latestSaveRef so save doesn't
 *   need to be a dep (would cause infinite loop via formData → save → effect)
 * - saveStatus added to autosave deps so 'saving'/'error' guards are
 *   evaluated fresh
 */
import { useState, useEffect, useCallback, useRef } from 'react';

const AUTOSAVE_DELAY_MS = 1500;

export default function useADLForm(initialData, saveFn, { disabled = false, validate = null } = {}) {
  const [formData, setFormData]     = useState(initialData || {});
  const [isDirty, setIsDirty]       = useState(false);
  const [saveStatus, setSaveStatus] = useState('idle'); // idle | saving | saved | error
  const [saveError, setSaveError]   = useState(null);

  const autosaveTimer      = useRef(null);
  const latestSaveFn       = useRef(saveFn);
  const lastSerializedRef  = useRef(JSON.stringify(initialData));
  const latestSaveRef      = useRef(null);
  const latestValidateRef  = useRef(validate);
  // Mirror of isDirty for the re-sync guard. Read via ref (not a dep) so
  // the dirty→clean transition doesn't re-run the re-sync effect against a
  // stale initialData. See header note (2026-06-18).
  const isDirtyRef         = useRef(isDirty);

  // Keep saveFn ref current without triggering re-renders
  useEffect(() => {
    latestSaveFn.current = saveFn;
  }, [saveFn]);

  // Keep validate ref current without making it a save() dependency
  useEffect(() => {
    latestValidateRef.current = validate;
  }, [validate]);

  // Keep isDirtyRef current without making it a re-sync dependency
  useEffect(() => {
    isDirtyRef.current = isDirty;
  }, [isDirty]);

  // Re-sync form when initialData changes (e.g. after a refresh) — but
  // NEVER clobber unsaved in-progress edits. Uses lastSerializedRef to
  // avoid JSON.stringify as a dep expression. See header note (2026-06-18).
  useEffect(() => {
    if (!initialData) return;
    const serialized = JSON.stringify(initialData);
    if (serialized === lastSerializedRef.current) return;

    // Dirty-state guard: an actively-editing form keeps the user's
    // unsaved input; a background refresh must not overwrite it. A clean
    // form adopts the incoming data (stays fresh). isDirtyRef is read here
    // rather than depended on so the dirty→clean transition itself can't
    // trigger a re-seed against stale data.
    if (isDirtyRef.current) return;

    lastSerializedRef.current = serialized;
    setFormData(initialData);
    setIsDirty(false);
    setSaveStatus('idle');
  }, [initialData]);

  // ── Field update ─────────────────────────────────────────
  const setField = useCallback((field, value) => {
    if (disabled) return;

    setFormData(prev => ({ ...prev, [field]: value }));
    setIsDirty(true);
    setSaveStatus('idle');
    setSaveError(null);
  }, [disabled]);

  // ── Bulk update (for checkbox groups, etc.) ──────────────
  const setFields = useCallback((updates) => {
    if (disabled) return;

    setFormData(prev => ({ ...prev, ...updates }));
    setIsDirty(true);
    setSaveStatus('idle');
    setSaveError(null);
  }, [disabled]);

  // ── Manual save ──────────────────────────────────────────
  const save = useCallback(async () => {
    if (!isDirty || disabled) return;

    if (autosaveTimer.current) {
      clearTimeout(autosaveTimer.current);
      autosaveTimer.current = null;
    }

    // Don't send a payload the server contract will reject (e.g. urination
    // without output_type). Leaves the form dirty so a later edit, once the
    // required field is present, re-triggers autosave — but prevents the
    // failed-save retry storm at the source by never firing the bad POST.
    // See header note (2026-06-24).
    if (latestValidateRef.current && !latestValidateRef.current(formData)) {
      return;
    }

    try {
      setSaveStatus('saving');
      await latestSaveFn.current(formData);
      setSaveStatus('saved');
      setIsDirty(false);

      setTimeout(() => setSaveStatus('idle'), 3000);
    } catch (err) {
      setSaveStatus('error');
      setSaveError(err.message || 'Save failed');
    }
  }, [isDirty, disabled, formData]);

  // Keep latestSaveRef current so the autosave effect always
  // calls the latest save without needing it as a dependency
  useEffect(() => {
    latestSaveRef.current = save;
  }, [save]);

  // ── Auto-save on field change ────────────────────────────
  // Early-return on 'error' as well as 'saving': a rejected save must not
  // reschedule itself (that was the retry storm). The next user edit resets
  // saveStatus to 'idle' via setField/setFields, which re-arms autosave.
  useEffect(() => {
    if (!isDirty || disabled || saveStatus === 'saving' || saveStatus === 'error') return;

    autosaveTimer.current = setTimeout(() => {
      latestSaveRef.current?.();
    }, AUTOSAVE_DELAY_MS);

    return () => {
      if (autosaveTimer.current) {
        clearTimeout(autosaveTimer.current);
      }
    };
  }, [formData, isDirty, disabled, saveStatus]);

  // ── Reset form to initial state ──────────────────────────
  const reset = useCallback(() => {
    setFormData(initialData || {});
    setIsDirty(false);
    setSaveStatus('idle');
    setSaveError(null);
  }, [initialData]);

  // ── Save status helpers for UI ───────────────────────────
  const isSaving = saveStatus === 'saving';
  const isSaved  = saveStatus === 'saved';
  const hasError = saveStatus === 'error';

  return {
    formData,
    setField,
    setFields,
    isDirty,
    save,
    reset,
    saveStatus,
    saveError,
    isSaving,
    isSaved,
    hasError,
  };
}